import { Component, Input, Output, EventEmitter } from '@angular/core';
import { FormGroup, FormArray, AbstractControl } from '@angular/forms';
import { Observable } from 'rxjs';

import { NavigationStepComponent } from './navigation-step.component';
import { PurchaseNavigationService } from './purchase-navigation.service';
import { WriteValueOverride } from './models';
import { TripInfo } from './models/trip-info.model';
import { State } from '@bhtp-client-api/eligibility';
import { TravelerDetailViewConfig, PolicyViewConfig, Mapper, StateSellingAbility, ModalService, AddressViewModel } from '@bhtp-client-web/component';

import { PurchaseData, Traveler, CustomerFullData } from './../shared';

@Component({
    selector: 'travelers-step',
    template: require('./travelers-step.component.html')
})
export class TravelersStepComponent extends NavigationStepComponent {
    @Input() public states: State[];
    @Input() public stateAvailability: StateSellingAbility[] = [];
    @Input() public agentCode: string;

    @Output() public policyHolderResidenceStateChanged: EventEmitter<State> = new EventEmitter<State>();

    public maxTravelers: number;

    private policyHolderForm: FormGroup;
    private policyHolderConfig: TravelerDetailViewConfig;

    private additionalTravelerForms: FormArray;
    private additionalTravelerConfig: TravelerDetailViewConfig;

    private tripInfoForm: FormGroup;
    private tripInfo: TripInfo;
    private policyConfig: PolicyViewConfig;

    constructor(navigationService: PurchaseNavigationService, public modalService: ModalService) {
        super(navigationService);
    }

    public buildForm(): void {
        // set the max number of travelers from the package config
        if (this.currentPackage && this.currentPackage.configuration) {
            if (this.currentPackage.configuration.maximumNumberTravelers) {
                this.maxTravelers = this.currentPackage.configuration.maximumNumberTravelers;
            }
        }

        // default maximum number of travelers
        if (!this.maxTravelers) {
            this.maxTravelers = 25;
        }

        if (this.fieldConfigs) {
            this.policyConfig = JSON.parse(JSON.stringify(this.fieldConfigs.policy));
            this.policyHolderConfig = JSON.parse(JSON.stringify(this.fieldConfigs.policyHolder));

            // hide gender on this step so it can be collected later
            if (this.policyHolderConfig.gender) {
                this.policyHolderConfig.gender.show = false;
            }

            // We disable this field because we already have the city, state, and zip of the customer from quick quote.
            this.policyHolderConfig.residence.editable = false;

            // if the email is not required, don't collect it on this page
            this.additionalTravelerConfig = JSON.parse(JSON.stringify(this.fieldConfigs.additionalTraveler));
            if (this.additionalTravelerConfig) {
                if (this.additionalTravelerConfig.email &&
                    !this.additionalTravelerConfig.email.required) {
                    this.additionalTravelerConfig.email.show = false;
                }

                // same with gender
                if (this.additionalTravelerConfig.gender) {
                    this.additionalTravelerConfig.gender.show = false;
                }
            }

            this.policyHolderForm = new FormGroup({ });

            this.additionalTravelerForms = new FormArray([]);
            if (this.currentPurchaseData.additionalTravelers) {
                this.currentPurchaseData.additionalTravelers.forEach(t => {
                    this.additionalTravelerForms.push(new FormGroup({}));
                });
            }

            this.currentPurchaseData.residenceState = this.currentPurchaseData.primaryTraveler.address.stateOrProvince;

            this.tripInfo = new TripInfo();
            this.tripInfo.departureDate = this.currentPurchaseData.departureDate;
            this.tripInfo.returnDate = this.currentPurchaseData.returnDate;
            this.tripInfo.depositDate = this.currentPurchaseData.depositDate;
            this.tripInfo.destinationCountry = this.currentPurchaseData.destinationCountry;
            this.tripInfoForm = new FormGroup({ });
        }

        this.setupFormChangesSubscriber();
    }

    public canContinue(): Observable<boolean> {
        return Observable.of(this.policyHolderForm.valid && this.tripInfoForm.valid && this.additionalTravelerForms.valid);
    }

    public writeValues(purchaseData: PurchaseData, override: WriteValueOverride = WriteValueOverride.Default): void {
        Mapper.copyValues(this.policyHolderForm.getRawValue(), purchaseData.primaryTraveler);

        let existingTravelers = purchaseData.additionalTravelers;
        purchaseData.additionalTravelers = [];
        this.additionalTravelerForms.controls.forEach((f: FormGroup) => {
            let additionalTraveler: Traveler = f.getRawValue();

            let matchingTraveler: Traveler = existingTravelers.find((target: Traveler) => {
                return target.correlationId === additionalTraveler.correlationId;
            });

            if (matchingTraveler) {
                 purchaseData.additionalTravelers.push(Mapper.copyValues(additionalTraveler, matchingTraveler));
            } else {
                purchaseData.additionalTravelers.push(additionalTraveler);
            }
        });

        this.tripInfo = this.tripInfoForm.value;
        if (this.tripInfo) {
            purchaseData.departureDate = this.tripInfo.departureDate;
            purchaseData.returnDate = this.tripInfo.returnDate;
            purchaseData.depositDate = this.tripInfo.depositDate;
            purchaseData.destinationCountry = this.tripInfo.destinationCountry;
        }
    }

    public quoteBeforeContinue(): boolean {
        return true;
    }

    public onAddTravelerClick(event: any): void {
        this.currentPurchaseData.additionalTravelers.push(new Traveler(false));
        this.additionalTravelerForms.push(new FormGroup({ }));
    }

    public onRemoveTravelerClick(event: any, index: number): void {
        this.currentPurchaseData.additionalTravelers.splice(index, 1);
        this.additionalTravelerForms.removeAt(index);
    }

    public mapCustomer(customer: CustomerFullData): void {
        if (this.currentPurchaseData && customer) {
            // make sure current data in the form isn't overwritten
            this.writeValues(this.currentPurchaseData);

            // customer data
            this.currentPurchaseData.primaryTraveler.firstName = customer.firstName;
            this.currentPurchaseData.primaryTraveler.lastName = customer.lastName;
            this.currentPurchaseData.primaryTraveler.birthDate = customer.birthDate;
            this.currentPurchaseData.primaryTraveler.gender = customer.gender;
            this.currentPurchaseData.customerId = customer.customerId;

            // contact info
            if (customer.phoneNumbers) {
                // only primary is returned so hardcoding map to 0 object of the array.
                this.currentPurchaseData.primaryTraveler.phoneNumber = customer.phoneNumbers.length > 0 ? customer.phoneNumbers[0].phoneNumber : null;
            }
            else {
                // clear previous values if phone number not found
                this.currentPurchaseData.primaryTraveler.phoneNumber = null;
            }

            if (customer.emailAddress) {
                this.currentPurchaseData.primaryTraveler.emailGroup.email = customer.emailAddress;
                this.currentPurchaseData.primaryTraveler.emailGroup.noEmail = false;
            }
            else {
                this.currentPurchaseData.primaryTraveler.emailGroup.email = customer.emailAddress;
                this.currentPurchaseData.primaryTraveler.emailGroup.noEmail = true;
            }

            // address data
            let originalSelectedResidenceState: string = this.currentPurchaseData.residenceState;

            if (customer.address) {
                // this.currentPurchaseData.primaryTraveler.address = customer.address;
                this.currentPurchaseData.primaryTraveler.address.address1 = customer.address.address1;
                this.currentPurchaseData.primaryTraveler.address.address2 = customer.address.address2;
                this.currentPurchaseData.primaryTraveler.address.city = customer.address.city;
                this.currentPurchaseData.primaryTraveler.address.postalCode = customer.address.postalCode;
                this.currentPurchaseData.residenceState = this.currentPurchaseData.primaryTraveler.address.stateOrProvince;
            }
            else {
                // clear previous values if address not found (leave the previously selected state if the user does not have
                //  a state on his/her profile.
                this.currentPurchaseData.primaryTraveler.address.address1 = null;
                this.currentPurchaseData.primaryTraveler.address.address2 = null;
                this.currentPurchaseData.primaryTraveler.address.city = null;
                this.currentPurchaseData.primaryTraveler.address.postalCode = null;
            }

            // Per BAs, keep the originally entered state because the agent is required to select a state to enter the purchase path
            this.currentPurchaseData.primaryTraveler.address.stateOrProvince = originalSelectedResidenceState;

            this.buildForm();
        }
    }

    private setupFormChangesSubscriber(): void {
        this.policyHolderForm.valueChanges
            .debounceTime(1000)
            .subscribe(
                (formValues: Traveler) => {
                    if (formValues) {
                        this.checkIfPolicyHolderResidenceStateChanged(formValues);
                    }
                },
                (error: Error) => {
                    // This should never happen, in the ng2 source error is never emitted
                    console.error(error);
                });
    }

    private checkIfPolicyHolderResidenceStateChanged(traveler: Traveler): void {
        if (!this.currentPurchaseData.primaryTraveler.address) {
            this.currentPurchaseData.primaryTraveler.address = new AddressViewModel();
        }

        if (this.states && traveler && traveler.address && traveler.address.stateOrProvince !== this.currentPurchaseData.primaryTraveler.address.stateOrProvince) {
            let targetState: string = traveler.address.stateOrProvince;
            let state: State;

            if (targetState) {
                state = this.states.find(state => state.isoCode2.toLowerCase().trim() === traveler.address.stateOrProvince.toLowerCase().trim());
            }

            if (state) {
                let validState: boolean = true;
                if (this.stateAvailability) {
                    let sellingAbility: StateSellingAbility = this.stateAvailability.find(s => s.isoCode2 === state.isoCode2);

                    // if the state can't be selected, clear the value and pop-up a modal
                    if (sellingAbility && !sellingAbility.canSell) {
                        this.modalService.showMessageModal('Product Unavailable', `The product you selected is not available in ${sellingAbility.name}. Please select a different product.`);
                        let stateControl: AbstractControl = (<FormGroup>this.policyHolderForm.controls['address']).controls['stateOrProvince'];
                        stateControl.patchValue(null);
                        stateControl.updateValueAndValidity();
                        validState = false;
                    }
                }

                if (validState) {
                    this.currentPurchaseData.residenceState = state.isoCode2;
                    this.currentPurchaseData.primaryTraveler.address.stateOrProvince = state.isoCode2;
                    this.policyHolderResidenceStateChanged.emit(state);
                }
            }
        }
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/typescript/purchase/travelers-step.component.ts